import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { getApiUrl } from '@/config';
import type { Technician } from '@shared/types';
import { AuthProvider, useAuth } from '@features/auth/context/AuthContext';
import { ThemeProvider } from '@shared/contexts/ThemeContext';
import { LayoutProvider, useLayout } from '@shared/contexts/LayoutContext';
import { SidebarLayout } from '@shared/components/Layout/SidebarLayout';
import { LoginPage } from '@features/auth/components/LoginPage';
import { PlanningDashboard } from '@features/planning/pages/PlanningDashboard';
import { TechnicianView } from '@features/dashboard/pages/TechnicianView';
import { WarehouseView } from '@features/inventory/pages/WarehouseView';
import { DashboardPage } from '@features/dashboard/pages/DashboardPage';
import { FleetPage } from '@features/fleet/pages/FleetPage';
import { InventoryPage } from '@features/inventory/pages/InventoryPage';
import { SettingsPage } from '@features/admin/pages/SettingsPage';
import { DEMO_TECHNICIANS } from '@/demoData';
import { InstallButton } from '@shared/components/InstallButton';
import { MyToaster } from '@shared/components/MyToaster';
import { isDemoDataDisabled } from '@shared/utils/demoDataControl';
import { APP_VERSION, COMMIT_HASH } from '@/version';
import type { Tool } from '@shared/types/inventory';

// Component that switches layouts based on Context
const AppContent = () => {
  const { layoutMode } = useLayout();
  const { user } = useAuth();
  const location = useLocation();

  // If sidebar mode is active AND user is authenticated AND not on login page
  const shouldUseSidebar = layoutMode === 'sidebar' && user && location.pathname !== '/login';

  return (
    <div className="relative min-h-screen overflow-hidden transition-colors duration-300">
      {/* Render Ambient Blobs ONLY if NOT in Sidebar Mode (Sidebar mode manages its own cleaner background) */}
      {!shouldUseSidebar && (
        <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob" />
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-32 left-1/3 w-96 h-96 bg-blue-500/20 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob animation-delay-4000" />
        </div>
      )}

      {shouldUseSidebar ? (
        <SidebarLayout>
          <AppRoutes />
        </SidebarLayout>
      ) : (
        <div className="relative z-10 flex flex-col min-h-screen">
          <div className="flex-grow">
            <AppRoutes />
          </div>
          {/* Global Footer (only for Top Nav mode) */}
          <footer className="py-6 text-center text-xs text-muted/50 font-medium">
            <p>ma-dsi.info 2026 © Tous droits réservés</p>
            <p className="mt-1">v{APP_VERSION} <span className="opacity-50 font-mono text-[10px]">({COMMIT_HASH})</span></p>
          </footer>
        </div>
      )}

      <InstallButton />
    </div>
  );
};

const AppRoutes = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [technicians, setTechnicians] = useState<Technician[]>(() =>
    isDemoDataDisabled() ? [] : DEMO_TECHNICIANS
  );
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [socketId, setSocketId] = useState<string | null>(null);
  const isRemoteUpdate = useRef(false);

  // Socket Helper
  const socketRef = useRef<any>(null);
  const socketIdRef = useRef<string | null>(null);

  // --- Tools Data Loading & Sync (States declared at top) ---
  const [isToolsLoaded, setIsToolsLoaded] = useState(false);
  const isRemoteToolUpdate = useRef(false);

  // --- Data Loading Logic (Existing) ---
  const loadData = useCallback(() => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    Promise.all([
      fetch(getApiUrl('/api/technicians'), { headers }),
      fetch(getApiUrl('/api/assignments'), { headers })
    ])
      .then(async ([techRes, assignRes]) => {
        if (techRes.status === 401 || techRes.status === 403) {
          console.warn('Authentication failed (403/401). Logging out...');
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.href = '/';
          throw new Error('AUTH_FAILED');
        }

        if (techRes.ok && assignRes.ok) {
          const techs = await techRes.json();
          const allAssignments = await assignRes.json();

          // Merge assignments into technicians
          const techsWithAssignments = techs.map((t: any) => ({
            ...t,
            assignments: allAssignments.filter((a: any) => a.technicianId === t.id)
          }));

          return techsWithAssignments;
        }
        throw new Error('Not found');
      })
      .then(data => {
        isRemoteUpdate.current = true;
        setTechnicians(data);
        setIsLoaded(true);
      })
      .catch((e) => {
        if (e.message === 'AUTH_FAILED') return;

        if (!isLoaded) {
          if (isDemoDataDisabled()) {
            console.warn('Backend unreachable and Demo Data is disabled. App will remain empty.');
            setIsLoaded(true); // Stop loading state, show empty
            return;
          }
          console.log('Using demo data, initializing server...');
          const token = localStorage.getItem('token');
          fetch(getApiUrl('/api/technicians'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(DEMO_TECHNICIANS),
          }).then(() => setIsLoaded(true));
        }
      });
  }, [isLoaded]);

  const loadTools = useCallback(() => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(getApiUrl('/api/tools'), { headers })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          console.warn('Authentication failed (403/401). Logging out...');
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.href = '/';
          throw new Error('AUTH_FAILED');
        }
        return res.ok ? res.json() : [];
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          isRemoteToolUpdate.current = true;
          setTools(data);
        } else {
          // Server returned empty array - trust server
          setTools([]);
        }
        setIsToolsLoaded(true);
      })
      .catch(err => {
        if (err.message === 'AUTH_FAILED') return;
        console.error('Failed to load tools', err);
        setTools([]);
        setIsToolsLoaded(true);
      });
  }, []);

  useEffect(() => {
    const socket = io(getApiUrl(''), {
      // Default transports (polling -> websocket) are safer for hosted envs
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to socket server', socket.id);
      socketIdRef.current = socket.id || null;
      setSocketId(socket.id || null);
      if (user && (user.role === 'admin' || user.role === 'planner')) {
        socket.emit('join_planners');
      }
    });

    socket.on('technicians_updated', () => {
      console.log('Socket: Technicians updated -> Refreshing...');
      loadData();
      window.dispatchEvent(new Event('refresh_backlog'));
    });

    socket.on('assignment_updated', (updatedAssignment: any) => {
      console.log('Socket: Assignment updated', updatedAssignment);
      loadData();
      window.dispatchEvent(new Event('refresh_backlog'));
    });

    socket.on('tools_updated', () => {
      console.log('Socket: Tools updated -> Refreshing...');
      loadTools();
    });

    socket.on('vehicles_updated', () => {
      // FleetPage handles its own fetching usually, but simpler to dispatch a custom event or use Context
      console.log('Socket: Vehicles updated -> Dispatching local event');
      window.dispatchEvent(new Event('vehicles_updated'));
    });

    return () => {
      socket.disconnect();
    };
  }, [user, loadData, loadTools]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadData();
    // const interval = setInterval(loadData, 60000);
    // return () => clearInterval(interval);
  }, [isAuthenticated, loadData]);

  useEffect(() => {
    if (!isLoaded || !isAuthenticated) return;
    if (isRemoteUpdate.current) {
      isRemoteUpdate.current = false;
      return;
    }
    const handler = setTimeout(() => {
      // Only Admin or Planner can save full state
      if (user?.role !== 'admin' && user?.role !== 'planner') return;

      const token = localStorage.getItem('token');
      fetch(getApiUrl('/api/technicians'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-socket-id': socketIdRef.current || ''
        },
        body: JSON.stringify(technicians),
      }).catch(err => console.error('Failed to save technicians', err));
    }, 500);
    return () => clearTimeout(handler);
  }, [technicians, isLoaded, user, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadTools();
    const interval = setInterval(loadTools, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [isAuthenticated, loadTools]);

  useEffect(() => {
    if (!isToolsLoaded || !isAuthenticated) return;
    if (isRemoteToolUpdate.current) {
      isRemoteToolUpdate.current = false;
      return;
    }
    const handler = setTimeout(() => {
      // Sync tools to server
      const token = localStorage.getItem('token');
      // Double check token existence
      if (!token) return;

      fetch(getApiUrl('/api/tools'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-socket-id': socketIdRef.current || ''
        },
        body: JSON.stringify(tools),
      }).catch(err => console.error('Failed to save tools', err));
    }, 1000); // 1s debounce
    return () => clearTimeout(handler);
  }, [tools, isToolsLoaded, isAuthenticated]);

  // --- Handlers (Existing) ---
  const handleUpdateTechnician = (techId: string, updates: Partial<Technician>) => {
    setTechnicians(prevTechs => prevTechs.map(tech =>
      tech.id === techId ? { ...tech, ...updates } : tech
    ));
  };

  const handleUpdateRemarque = (assignmentId: string, remarque: string) => {
    setTechnicians(prevTechs =>
      prevTechs.map(tech => ({
        ...tech,
        assignments: tech.assignments.map(assignment =>
          assignment.id === assignmentId ? { ...assignment, remarque } : assignment
        ),
      }))
    );
  };

  const handleStartWorksite = (assignmentId: string) => {
    setTechnicians(prevTechs =>
      prevTechs.map(tech => ({
        ...tech,
        assignments: tech.assignments.map(assignment =>
          assignment.id === assignmentId
            ? { ...assignment, status: 'in-progress', startedAt: new Date().toISOString() }
            : assignment
        ),
      }))
    );
  };

  const handleFinishWorksite = (assignmentId: string) => {
    setTechnicians(prevTechs =>
      prevTechs.map(tech => ({
        ...tech,
        assignments: tech.assignments.map(assignment =>
          assignment.id === assignmentId
            ? { ...assignment, status: 'completed-by-tech', completedAt: new Date().toISOString() }
            : assignment
        ),
      }))
    );
  };

  const handleFinishWorksiteWithConfirm = (assignmentId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir terminer ce chantier ?')) {
      handleFinishWorksite(assignmentId);
    }
  };

  const handleCloseWorksite = (assignmentId: string) => {
    if (!user) return;
    setTechnicians(prevTechs =>
      prevTechs.map(tech => ({
        ...tech,
        assignments: tech.assignments.map(assignment =>
          assignment.id === assignmentId
            ? { ...assignment, status: 'closed', closedAt: new Date().toISOString(), closedBy: user.id }
            : assignment
        ),
      }))
    );
  };

  const hasAccess = (module: 'planning' | 'fleet' | 'tooling') => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'magasinier' && module === 'tooling') return true;
    const perms = user?.permissions || {
      planning: user?.role === 'planner' ? 'write' : 'none',
      fleet: user?.role === 'planner' ? 'write' : 'none',
      tooling: user?.role === 'planner' ? 'write' : 'none'
    };
    return perms[module] !== 'none';
  };

  if (!isAuthenticated || !user) {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route path="/" element={
        user.technicianId
          ? <TechnicianView
            allTechnicians={technicians}
            onUpdateTechnician={handleUpdateTechnician}
            onUpdateRemarque={handleUpdateRemarque}
            onStartWorksite={handleStartWorksite}
            onFinishWorksite={handleFinishWorksiteWithConfirm}
            tools={tools}
            onUpdateTools={setTools}
          />
          : user.role === 'magasinier'
            ? <WarehouseView
              technicians={technicians}
              tools={tools}
              onUpdateTools={setTools}
            />
            : (user.role === 'admin' || user.role === 'planner' || hasAccess('planning') || hasAccess('fleet') || hasAccess('tooling'))
              ? <DashboardPage technicians={technicians} tools={tools} />
              : <div className="min-h-screen flex items-center justify-center p-4">
                <div className="glass-panel p-8 text-center">
                  <p className="text-amber-400">Accès restreint. Veuillez contacter un administrateur.</p>
                  <button onClick={logout} className="mt-4 glass-button text-gray-400">
                    Déconnexion
                  </button>
                </div>
              </div>
      } />

      <Route path="/planning" element={
        hasAccess('planning')
          ? <PlanningDashboard
            initialTechnicians={technicians}
            onUpdateTechnician={handleUpdateTechnician}
            onCloseWorksite={handleCloseWorksite}
            onStartWorksite={handleStartWorksite}
            onFinishWorksite={handleFinishWorksiteWithConfirm}
            socketId={socketId}
          />
          : <Navigate to="/" />
      } />

      <Route path="/fleet" element={hasAccess('fleet') ? <FleetPage /> : <Navigate to="/" />} />
      <Route path="/inventory" element={hasAccess('tooling') ? <InventoryPage technicians={technicians} tools={tools} onUpdateTools={setTools} /> : <Navigate to="/" />} />
      <Route path="/settings" element={user.role === 'admin' ? <SettingsPage technicians={technicians} setTechnicians={setTechnicians} /> : <Navigate to="/" />} />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LayoutProvider>
        <MyToaster />
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </LayoutProvider>
    </ThemeProvider>
  );
}

export default App;
